import { Button, ServiceError } from "@shapr3d/frontend-lib";
import { toast } from "react-hot-toast";

import useNavigation from "../../../../hooks/navigation";
import useAccount from "../../../../hooks/useAccount";
import { useResendVerificationEmail } from "../../../../hooks/useAuth";
import { useTracker } from "../../../../hooks/useTracker";
import useTranslation from "../../../../hooks/useTranslation";
import { Modal } from "../../../../lib/modal";
import { ServiceErrorCode } from "../../../../types";
import { GenericErrorModal } from "../../../Modal/Modal";
import { NewZendeskTicketLink } from "../../../NewZendeskTicketLink/NewZendeskTicketLink";

import style from "./CreateCheckoutErrorModal.module.css";

const ResendEmailVerification: React.FC = () => {
  const { t, m } = useTranslation();
  const tracker = useTracker();
  const navigation = useNavigation();
  const { data: accountData } = useAccount();

  const { mutate: mutateResendVerificationEmail, isPending: isResendVerificationEmailPending } =
    useResendVerificationEmail();

  return (
    <div className={style.ctaRow}>
      <Button
        disabled={isResendVerificationEmailPending}
        onClick={async () => {
          tracker.trackRequestVerificationEmailClicked({ location: "checkout" });
          mutateResendVerificationEmail();
          toast.success(
            <span>
              {t(m.Checkout.Toast.VerificationEmailSent)}
              <b>{accountData?.email}</b>
            </span>
          );
          navigation.goToProfile();
        }}
      >
        {t(m.Checkout.ErrorModal.ResendVerificationEmail)}
      </Button>
    </div>
  );
};

export const CheckoutErrorModal: React.FC<{
  error: Error | null;
  open: boolean;
  onClose: () => void;
}> = ({ error, open, onClose }) => {
  const { t, m } = useTranslation();
  const tracker = useTracker();

  if (error instanceof ServiceError) {
    if (error.hasCode(ServiceErrorCode.UserAlreadyHasSubscription)) {
      tracker.trackCheckoutFlowLoadError("already_has_subscription");
      return (
        <Modal
          title={t(m.Checkout.ErrorModal.Title)}
          open={open}
          onClose={onClose}
          telemetryName="checkout_error_already_subscribed"
        >
          <span>{t(m.Checkout.ErrorModal.AlreadyHasSubscription)}</span>
        </Modal>
      );
    }
    if (error.hasCode(ServiceErrorCode.UnsupportedCountry)) {
      tracker.trackCheckoutFlowLoadError("unsupported_country");
      return (
        <Modal
          title={t(m.Checkout.ErrorModal.Title)}
          open={open}
          onClose={onClose}
          telemetryName="checkout_error_unsupported_country"
        >
          <span>{t(m.Checkout.ErrorModal.UnsupportedCountry)}</span>
        </Modal>
      );
    }
    if (error.hasCode(ServiceErrorCode.EmailNotVerified)) {
      tracker.trackCheckoutFlowLoadError("unverified_email");
      return (
        <Modal
          title={t(m.Checkout.ErrorModal.Title)}
          open={open}
          size="small"
          onClose={onClose}
          telemetryName="checkout_error_unverified_email"
        >
          <div className={style.verifyEmailContainer}>
            <p>{t(m.Checkout.ErrorModal.VerifyEmail)}</p>
            <ResendEmailVerification />
          </div>
        </Modal>
      );
    }
    if (error.hasCode(ServiceErrorCode.TierUnavailable)) {
      tracker.trackCheckoutFlowLoadError("tier_unavailable");
      return (
        <Modal
          title={t(m.Checkout.ErrorModal.Title)}
          open={open}
          onClose={onClose}
          telemetryName="checkout_error_tier_unavailable"
        >
          <span>{t(m.Checkout.ErrorModal.TierUnavailable)}</span>
        </Modal>
      );
    }

    if (error.hasCode(ServiceErrorCode.CheckoutNotAllowed)) {
      tracker.trackCheckoutFlowLoadError("not_allowed");
      return (
        <Modal
          title={t(m.Checkout.ErrorModal.TooManyAttempts)}
          open={open}
          onClose={onClose}
          telemetryName="checkout_error_not_allowed"
        >
          {t(m.Checkout.ErrorModal.TryAgainLater)}{" "}
          <NewZendeskTicketLink>{t(m.Checkout.ErrorModal.ThisLink)}</NewZendeskTicketLink>.
        </Modal>
      );
    }
  }

  if (open) {
    tracker.trackCheckoutFlowLoadError("unknown");
  }
  return <GenericErrorModal open={open} onClose={onClose} telemetryName="checkout_error_unknown" />;
};
